<template>
  <WdsStack direction-column gap-custom="2rem">
    <WdsAlert v-if="!hasOnboarded" icon info>
      <template #default>{{ $t('Invite pending') }}</template>

      <template #description>
        {{
          $t(
            'Some information is only available after they have accepted your invitation to join Worksome.',
          )
        }}
      </template>
    </WdsAlert>

    <WdsStack v-if="props.trustedContact?.onboardingStatus" direction-row gap-sm >
      <WdsStack direction-column gap-md class="tw-basis-full">
        <WdsText :sub-text="$t('Onboarding')" />
        <WdsStack direction-column gap-sm>
          <WdsStack align-center gap-sm>
            <WdsStack class="tw-basis-1/2 tw-items-center tw-flex">
              <span
                class="tw-mr-1 tw-text-lg"
                :class="[
                  'tw-mr-1 tw-text-lg',
                  props.trustedContact?.onboardingStatus ===
                    TrustedContactOnboardingStatus.Completed && 'tw-text-green-500',
                  props.trustedContact?.onboardingStatus ===
                    TrustedContactOnboardingStatus.Pending && 'tw-text-orange-500',
                ]"
              >
                •
              </span>
              <WdsText>{{ $t('Required Documents') }}</WdsText>
            </WdsStack>
            <WdsStack class="tw-basis-1/2">
              <WdsLabel
                sm
                subtle
                :success="
                  props.trustedContact?.onboardingStatus ===
                  TrustedContactOnboardingStatus.Completed
                "
                :warning="
                  props.trustedContact?.onboardingStatus === TrustedContactOnboardingStatus.Pending
                "
                class="tw-capitalize"
              >
                {{ $t(props.trustedContact?.onboardingStatus.toLowerCase()) }}
              </WdsLabel>
            </WdsStack>
          </WdsStack>
        </WdsStack>
      </WdsStack>
    </WdsStack>

    <WdsStack direction-row gap-sm>
      <WdsStack direction-column gap-md class="tw-basis-1/2">
        <WdsText :sub-text="$t('Location & Market')" />

        <WdsStack direction-column gap-md>
          <LocationDetails
            v-if="hasOnboarded"
            :address="addressFragment"
            :market="marketFragment"
            :currency="workerFragment.currency"
          />

          <WdsStack align-center gap-sm>
            <WdsIcon
              class="tw-flex-shrink-0"
              :size="16"
              :name="WdsIconNameEnum.BUILDING_INSIGHTS_1"
            />

            <WdsText data-test="business-entity-text" :text="businessEntityName" primary />
          </WdsStack>
        </WdsStack>
      </WdsStack>

      <WdsStack direction-column gap-md class="tw-basis-1/2">
        <WdsText :sub-text="$t('Contact Information')" />

        <WdsStack direction-column gap-sm>
          <WdsStack v-if="hasOnboarded && workerFragment?.phone" align-center gap-sm>
            <WdsIcon class="tw-flex-shrink-0" :size="16" :name="WdsIconNameEnum.PHONE" />

            <div class="tw-line-clamp-1 tw-break-all">
              <WdsButtonGhost
                class="tw-group tw-flex tw-items-center tw-gap-sm tw-w-full tw-max-w-fit"
                :class="{ 'copied-text': copiedText.phone }"
                @click="copyText('phone', workerFragment.phone)"
              >
                <span class="tw-line-clamp-1 tw-text-left" :title="workerFragment.phone">
                  {{ workerFragment.phone }}
                </span>
                <div v-if="!copiedText.phone" class="tw-invisible group-hover:tw-visible">
                  <WdsIcon :name="WdsIconNameEnum.COPY" :size="16" />
                </div>
                <WdsText v-if="copiedText.phone" tertiary class="tw-break-normal">
                  {{ $t('Copied!') }}
                </WdsText>
              </WdsButtonGhost>
            </div>
          </WdsStack>

          <WdsStack align-center gap-sm>
            <WdsIcon class="tw-flex-shrink-0" :size="16" :name="WdsIconNameEnum.EMAIL" />

            <div class="tw-line-clamp-1 tw-break-all">
              <WdsButtonGhost
                class="tw-group tw-flex tw-items-center tw-gap-sm tw-w-full tw-max-w-fit"
                :class="{ 'copied-text': copiedText.email }"
                @click="copyText('email', workerFragment.email)"
              >
                <span
                  v-if="!!workerFragment?.email"
                  class="tw-line-clamp-1 tw-text-left"
                  :title="workerFragment?.email"
                >
                  {{ workerFragment.email }}
                </span>
                <div v-if="!copiedText.email" class="tw-invisible group-hover:tw-visible">
                  <WdsIcon :name="WdsIconNameEnum.COPY" :size="16" />
                </div>
                <WdsText v-if="copiedText.email" tertiary class="tw-break-normal">
                  {{ $t('Copied!') }}
                </WdsText>
              </WdsButtonGhost>
            </div>
          </WdsStack>
        </WdsStack>
      </WdsStack>
    </WdsStack>

    <WdsStack direction-column gap-lg>
      <WdsStack v-if="hasTc" direction-column gap-md>
        <WdsText secondary>{{ $t('Links') }} ({{ $t('not shared with worker') }})</WdsText>

        <WdsStack direction-column gap-sm class="-tw-ml-1">
          <a
            v-for="link in props.trustedContact?.links"
            :key="link"
            :href="Urls.formatWithProtocol(link)"
            target="_blank"
          >
            <WdsButtonGhost class="tw-w-full">
              <div class="tw-flex tw-justify-between tw-items-center tw-w-full">
                <span
                  class="tw-text-base tw-font-normal tw-text-gray-900 tw-line-clamp-1 tw-break-all tw-text-left"
                  :title="link"
                >
                  {{ link }}
                </span>
                <WdsIcon
                  :size="16"
                  :name="WdsIconNameEnum.CHEVRON_RIGHT"
                  class="tw-ml-1 tw-text-gray-600 tw-shrink-0"
                />
              </div>
            </WdsButtonGhost>
          </a>

          <div v-if="showAddLinksButton">
            <WdsButtonGhost primary @click="emit('openEditModal')">
              <WdsStack align-center gap-sm>
                <WdsIcon :name="WdsIconNameEnum.PLUS" :size="18" />
                {{ $t('Add link') }}
              </WdsStack>
            </WdsButtonGhost>
          </div>
        </WdsStack>
      </WdsStack>

      <WdsStack v-if="hasOnboarded && hasLinks" direction-column gap-md>
        <WdsText :text="$t('Worker Links')" secondary />
        <WdsStack direction-column gap-sm class="-tw-ml-1">
          <a
            v-for="link in workerFragment?.profile?.links"
            :key="link.url"
            :href="Urls.formatWithProtocol(link.url)"
            target="_blank"
          >
            <WdsButtonGhost class="tw-w-full">
              <div class="tw-flex tw-justify-between tw-items-center tw-w-full">
                <span
                  class="tw-text-base tw-font-normal tw-text-gray-900 tw-line-clamp-1 tw-break-all tw-text-left"
                  :title="link.title"
                >
                  {{ link.title }}
                </span>
                <WdsIcon
                  :size="16"
                  :name="WdsIconNameEnum.CHEVRON_RIGHT"
                  class="tw-ml-1 tw-text-gray-600 tw-shrink-0"
                />
              </div>
            </WdsButtonGhost>
          </a>
        </WdsStack>
      </WdsStack>

      <WdsStack v-if="hasTc" direction-column gap-md>
        <WdsText secondary>{{ $t('External ID') }} ({{ $t('not shared with worker') }})</WdsText>
        <WdsStack direction-column gap-sm class="-tw-ml-1.5">
          <WdsButtonGhost
            v-if="props.trustedContact?.externalIdentifier"
            class="tw-group tw-flex tw-items-center tw-gap-sm tw-w-full tw-max-w-fit"
            :class="{ 'copied-text': copiedText.externalIdentifier }"
            @click="copyText('externalIdentifier', props.trustedContact?.externalIdentifier)"
          >
            <span
              class="tw-line-clamp-1 tw-text-left"
              :title="props.trustedContact?.externalIdentifier"
            >
              {{ props.trustedContact?.externalIdentifier }}
            </span>
            <div v-if="!copiedText.externalIdentifier" class="tw-invisible group-hover:tw-visible">
              <WdsIcon :name="WdsIconNameEnum.COPY" :size="16" />
            </div>
            <WdsText v-if="copiedText.externalIdentifier" tertiary class="tw-break-normal">
              {{ $t('Copied!') }}
            </WdsText>
          </WdsButtonGhost>

          <div v-if="showAddExternalIdButton">
            <WdsButtonGhost primary @click="emit('openEditModal')">
              <WdsStack align-center gap-sm>
                <WdsIcon :name="WdsIconNameEnum.PLUS" :size="18" />
                {{ $t('Add external ID') }}
              </WdsStack>
            </WdsButtonGhost>
          </div>
        </WdsStack>
      </WdsStack>

      <WdsStack v-if="hasTc" direction-column gap-md>
        <WdsText secondary>
          {{ $t('Company attachments') }} ({{ $t('not shared with worker') }})
        </WdsText>
        <WdsStack direction-column gap-sm class="-tw-ml-1.5">
          <a
            v-for="attachment in props.trustedContact?.attachments"
            :key="attachment.url"
            :href="attachment.url"
            target="_blank"
          >
            <WdsButtonGhost class="tw-w-full">
              <div class="tw-flex tw-justify-between tw-items-center tw-w-full">
                <div class="tw-gap-xs tw-items-center tw-line-clamp-1 tw-break-all !tw-flex">
                  <WdsIcon
                    :size="14"
                    :name="WdsIconNameEnum.DOCUMENT"
                    class="tw-text-gray-900 tw-flex-shrink-0"
                  />
                  <span class="tw-line-clamp-1 tw-mr-1" :title="attachment.name">
                    <WdsText :text="attachment.name" />
                  </span>
                </div>
                <WdsIcon
                  :name="WdsIconNameEnum.DOWNLOAD"
                  :size="16"
                  class="tw-text-gray-600 tw-flex-shrink-0"
                />
              </div>
            </WdsButtonGhost>
          </a>

          <div v-if="showAddAttachmentsButton">
            <WdsButtonGhost primary @click="emit('openEditModal')">
              <WdsStack align-center gap-sm>
                <WdsIcon :name="WdsIconNameEnum.PLUS" :size="18" />
                {{ $t('Add file') }}
              </WdsStack>
            </WdsButtonGhost>
          </div>
        </WdsStack>
      </WdsStack>

      <WdsStack v-if="hasTc" direction-column gap-md>
        <WdsText :text="$t('Skills added by your company')" secondary />
        <WdsStack wrap justify-left gap-sm>
          <WdsLabel
            v-for="skill of props.trustedContact?.skills"
            :key="skill.id"
            :label="skill.name"
            subtle
            primary
            md
          />

          <div v-if="showAddSkillsButton">
            <WdsButtonGhost primary @click="emit('openEditModal')">
              <WdsStack align-center gap-sm>
                <WdsIcon :name="WdsIconNameEnum.PLUS" :size="18" />
                {{ $t('Add skill') }}
              </WdsStack>
            </WdsButtonGhost>
          </div>
        </WdsStack>
      </WdsStack>

      <WdsStack v-if="hasOnboarded && workerFragment?.skills?.length > 0" direction-column gap-md>
        <WdsText :text="$t('Skills added by the Worker')" secondary />
        <WdsStack wrap justify-left gap-sm>
          <WdsLabel
            v-for="skill of workerFragment?.skills"
            :key="skill.id"
            :label="skill.name"
            subtle
            md
          />
        </WdsStack>
      </WdsStack>
    </WdsStack>

    <WdsStack v-if="hasTc" direction-column gap-md>
      <WdsText :text="$t('Custom fields')" secondary />
      <div v-if="showAddCustomFieldsButton">
        <WdsButtonGhost primary @click="emit('openEditModal')">
          <WdsStack align-center gap-sm>
            <WdsIcon :name="WdsIconNameEnum.PLUS" :size="18" />
            {{ $t('Fill out custom fields') }}
          </WdsStack>
        </WdsButtonGhost>
      </div>
      <CustomFieldValuesDisplay
        v-else-if="customFieldValues && customFieldValues?.length > 0"
        :custom-field-values="customFieldValues"
      />
    </WdsStack>

    <WdsStack v-if="hasTc" direction-column gap-xs>
      <WdsText :text="invitedAt" secondary />

      <WdsText :text="`${$t('By')} ${inviteByText}`" secondary />
    </WdsStack>
  </WdsStack>
</template>

<script setup lang="ts">
import { computed, ComputedRef, defineProps, inject, ref } from 'vue'
import { useClipboard } from '@vueuse/core'
import {
  WdsAlert,
  WdsButtonGhost,
  WdsIcon,
  WdsIconNameEnum,
  WdsLabel,
  WdsStack,
  WdsText,
} from '@worksome/wds-components'
import Urls from '@/utils/urls'
import { WORKER_VIEW_FRAGMENT } from '@/components/WorkerDetailPanel/graphql/fragments'
import CustomFieldValuesDisplay from '@/components/Fieldable/CustomFieldValuesDisplay.vue'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { FragmentType, useFragment } from '@/gql'
import useDate from '@/composables/useDate'
import { ViewOnlyInterface, viewOnlyKey } from '@/components/WorkerDetailPanel/injectionKeys'
import LocationDetails from '@/components/LocationDetails.vue'
import { TrustedContactViewFragmentFragment, TrustedContactOnboardingStatus } from '@/gql/graphql'

const { copy } = useClipboard()

const props = defineProps<{
  worker: FragmentType<typeof WORKER_VIEW_FRAGMENT>
  trustedContact?: TrustedContactViewFragmentFragment | null
}>()

const { viewOnly } = inject<ViewOnlyInterface>(viewOnlyKey, { viewOnly: ref(false) })
const emit = defineEmits(['openEditModal'])

const workerFragment = computed(() => useFragment(WORKER_VIEW_FRAGMENT, props.worker))

const hasTc: ComputedRef<boolean> = computed(() => !!props.trustedContact)

const hasOnboarded = computed(() => props.trustedContact?.status !== 'INVITED')

const showAddLinksButton = computed(
  () =>
    !viewOnly.value &&
    props.trustedContact?.links.length === 0 &&
    props.trustedContact?.viewerCanUpdate,
)

const showAddAttachmentsButton = computed(
  () =>
    !viewOnly.value &&
    props.trustedContact?.attachments.length === 0 &&
    props.trustedContact?.viewerCanUpdate,
)

const showAddExternalIdButton = computed(
  () =>
    !viewOnly.value &&
    !props.trustedContact?.externalIdentifier &&
    props.trustedContact?.viewerCanUpdate,
)

const showAddCustomFieldsButton = computed(
  () =>
    !viewOnly.value &&
    props.trustedContact?.customFieldValues.length === 0 &&
    props.trustedContact?.viewerCanUpdate,
)

const hasLinks = computed(() => (workerFragment.value?.profile?.links?.length ?? 0) > 0)

const showAddSkillsButton = computed(
  () =>
    !viewOnly.value &&
    props.trustedContact?.skills.length === 0 &&
    props.trustedContact?.viewerCanUpdate,
)

const addressFragment = computed(() => {
  const { address } = workerFragment.value ?? {}

  return address ?? {}
})

const marketFragment = computed(() => {
  const { market } = workerFragment.value ?? {}

  return market
})

const businessEntityName = computed(() => {
  if (!hasOnboarded.value || !workerFragment.value?.primaryBusinessEntity?.name) {
    return '–'
  }

  return workerFragment.value?.primaryBusinessEntity.name
})

const invitedAt = computed(() => {
  if (!props.trustedContact?.invitedAt) {
    return undefined // Typescript thing
  }

  const { date } = useDate(props.trustedContact?.invitedAt, 'MMM DD, YYYY')

  return `${$t('Trusted Contact was invited on')} ${date.value}`
})

const inviteByText = computed(
  () => props.trustedContact?.invitedByUser?.name ?? $t('Worksome System'),
)

const copiedText = ref({
  phone: false,
  email: false,
  externalIdentifier: false,
})

const copyText = (type, text) => {
  copy(text)
  copiedText.value[type] = true
  setTimeout(() => {
    copiedText.value[type] = false
  }, 1500)
}

const customFieldValues = computed(() => props.trustedContact?.customFieldValues)
</script>

<style scoped lang="scss">
:deep(.wds-button-ghost.copied-text) {
  &:hover {
    background-color: transparent !important;
    @apply tw-ease-in;
    @apply tw-duration-300;
  }
}
</style>
